/**
 * CivilHound paper identifiers (ID-001). Format is agency-configurable.
 * Default: MMDDYY-N — the date the paper was received, then the count of
 * papers entered that day (092726-1, 092726-2 …). The sequence resets DAILY
 * when the format contains {DD}, otherwise annually (e.g. CCSO-26-CIVIL-000001).
 *
 * Tokens: {PREFIX} {YYYY} {YY} {MM} {DD} {SEQ} {SEQn} (n = zero-pad width, e.g. {SEQ6})
 */

export const DEFAULT_PAPER_NUMBER_FORMAT = '{MM}{DD}{YY}-{SEQ}';

/** Does this format number papers per day (sequence resets each day)? */
export const isDailyFormat = (format: string) => format.includes('{DD}');

export function formatPaperNumber(format: string, prefix: string, date: number | { year: number; month?: number; day?: number }, seq: number): string {
  if (!Number.isInteger(seq) || seq < 1) throw new Error('Sequence must be a positive integer');
  const d = typeof date === 'number' ? { year: date } : date;
  if ((format.includes('{MM}') && !d.month) || (format.includes('{DD}') && !d.day)) throw new Error('Month and day are required for this number format');
  const out = format
    .replaceAll('{PREFIX}', prefix)
    .replaceAll('{YYYY}', String(d.year))
    .replaceAll('{YY}', String(d.year % 100).padStart(2, '0'))
    .replaceAll('{MM}', String(d.month ?? 0).padStart(2, '0'))
    .replaceAll('{DD}', String(d.day ?? 0).padStart(2, '0'))
    .replace(/\{SEQ(\d*)\}/g, (_m, w: string) => String(seq).padStart(w ? Number(w) : 1, '0'));
  if (!/^[A-Z0-9-]+$/.test(out)) throw new Error(`Paper number "${out}" must contain only A–Z, 0–9 and "-" (barcode-safe)`);
  return out;
}

/** Barcode payload contains ONLY the paper identifier (ID-002). */
export function barcodePayload(paperNumber: string): string {
  return paperNumber;
}

/** Normalize scanner/camera input to a paper number. */
export function normalizeScan(raw: string): string {
  return raw.trim().toUpperCase().replace(/\s+/g, '');
}

/** "Doe, John A." → "Doe"; "John Q. Doe Jr." → "Doe". For labels and cover sheets. */
export function lastNameOf(fullName: string): string {
  const n = fullName.trim();
  if (n.includes(',')) return n.split(',')[0]!.trim();
  const words = n.split(/\s+/).filter((w) => !/^(jr|sr|ii|iii|iv|v)\.?$/i.test(w));
  return words[words.length - 1] ?? n;
}
